import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Star, Plus, Pencil, Trash2, Check, X, Search, Filter, Camera, Image as ImageIcon } from 'lucide-react';
import { apiClient, publicApi, formatApiError } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ReviewPhotos } from './PhotoLightbox';

const STATUS_META = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
};

const EMPTY_FORM = {
  author_name: '',
  author_email: '',
  rating: 5,
  comment: '',
  product_name: '',
  photos: [],
  status: 'approved',
};

function StarInput({ rating, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className="cursor-pointer"
        >
          <Star
            size={26}
            className={i <= rating ? 'text-[#00aaff]' : 'text-gray-200'}
            fill={i <= rating ? '#00aaff' : '#e5e7eb'}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewEditorModal({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              author_name: initial.author_name || '',
              author_email: initial.author_email || '',
              rating: initial.rating || 5,
              comment: initial.comment || '',
              product_name: initial.product_name || '',
              photos: Array.isArray(initial.photos) ? [...initial.photos] : [],
              status: initial.status || 'approved',
            }
          : EMPTY_FORM
      );
    }
  }, [open, initial]);

  if (!open) return null;

  const handleUploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - form.photos.length;
    if (remaining <= 0) {
      toast.error('10 photos maximum');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, remaining)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await publicApi.post('/upload/review-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) urls.push(data.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
      if (urls.length) toast.success(`${urls.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author_name || !form.comment || !form.rating) {
      toast.error('Nom, note et commentaire sont requis');
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        const { data } = await apiClient.put(`/admin/reviews/${initial.id}`, form);
        toast.success('Avis modifié');
        onSaved && onSaved(data, 'update');
      } else {
        const { data } = await apiClient.post('/admin/reviews', form);
        toast.success('Avis créé');
        onSaved && onSaved(data, 'create');
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="review-editor-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-black text-gray-900">
            {initial?.id ? "Modifier l'avis" : 'Nouvel avis'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"
            data-testid="review-editor-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Nom de l'auteur *"
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              required
              className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
              data-testid="editor-author-name"
            />
            <input
              type="email"
              placeholder="Email auteur (optionnel)"
              value={form.author_email}
              onChange={(e) => setForm({ ...form, author_email: e.target.value })}
              className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
              data-testid="editor-author-email"
            />
          </div>

          <input
            type="text"
            placeholder="Produit associé (optionnel)"
            value={form.product_name}
            onChange={(e) => setForm({ ...form, product_name: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
            data-testid="editor-product-name"
          />

          <div>
            <p className="text-sm text-gray-500 mb-2">Note</p>
            <StarInput rating={form.rating} onChange={(r) => setForm({ ...form, rating: r })} />
          </div>

          <textarea
            placeholder="Commentaire *"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            required
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] resize-none"
            data-testid="editor-comment"
          />

          {/* Status */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Statut</p>
            <div className="flex gap-2">
              {['pending', 'approved', 'rejected'].map((s) => {
                const meta = STATUS_META[s];
                const active = form.status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, status: s })}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      active
                        ? 'bg-[#00aaff] text-white shadow-md shadow-[#00aaff]/30'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`editor-status-${s}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photos */}
          <div>
            <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
              <ImageIcon size={15} /> Photos ({form.photos.length}/10)
            </p>
            <div className="flex flex-wrap gap-3">
              {form.photos.map((url, idx) => (
                <div
                  key={idx}
                  className="relative group w-20 h-20 rounded-xl overflow-hidden ring-1 ring-gray-200"
                >
                  <img src={url} alt="photo" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`editor-remove-photo-${idx}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              {form.photos.length < 10 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#00aaff] hover:bg-[#00aaff]/5 text-gray-400 hover:text-[#00aaff] flex flex-col items-center justify-center transition-colors disabled:opacity-60"
                  data-testid="editor-add-photo"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Camera size={20} />
                      <span className="text-[10px] font-medium mt-1">Ajouter</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={handleUploadFiles}
              className="hidden"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-xl font-semibold hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 bg-[#00aaff] hover:bg-[#0088dd] text-white px-5 py-3 rounded-xl font-bold disabled:opacity-60"
              data-testid="editor-save"
            >
              {saving ? 'Enregistrement…' : initial?.id ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/admin/reviews/all');
      setReviews(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const stats = useMemo(
    () => ({
      all: reviews.length,
      pending: reviews.filter((r) => r.status === 'pending').length,
      approved: reviews.filter((r) => r.status === 'approved').length,
      rejected: reviews.filter((r) => r.status === 'rejected').length,
    }),
    [reviews]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reviews.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      return [r.author_name, r.author_email, r.comment, r.product_name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [reviews, filter, search]);

  const onQuickStatus = async (review, status) => {
    try {
      await apiClient.put(`/admin/reviews/${review.id}/status?status=${status}`);
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, status } : r)));
      toast.success('Statut mis à jour');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (review) => {
    if (!window.confirm(`Supprimer l'avis de ${review.author_name} ?`)) return;
    try {
      await apiClient.delete(`/admin/reviews/${review.id}`);
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
      toast.success('Avis supprimé');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onSaved = (doc, mode) => {
    if (mode === 'create') setReviews((prev) => [doc, ...prev]);
    else setReviews((prev) => prev.map((r) => (r.id === doc.id ? doc : r)));
  };

  const filterPills = [
    { id: 'all', label: 'Tous', color: 'bg-gray-900' },
    { id: 'pending', label: 'En attente', color: 'bg-amber-500' },
    { id: 'approved', label: 'Approuvés', color: 'bg-green-500' },
    { id: 'rejected', label: 'Refusés', color: 'bg-red-500' },
  ];

  return (
    <div className="space-y-5" data-testid="admin-reviews-manager">
      {/* Header + stats + create */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Gestion des avis</h2>
            <p className="text-sm text-gray-500">Approuver, modifier, créer et filtrer les avis clients</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
            className="inline-flex items-center gap-2 bg-[#00aaff] hover:bg-[#0088dd] text-white px-5 py-2.5 rounded-xl font-bold shadow-md shadow-[#00aaff]/30"
            data-testid="admin-review-create-btn"
          >
            <Plus size={16} /> Nouvel avis
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {filterPills.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setFilter(p.id)}
              className={`rounded-xl p-4 text-left transition-all border ${
                filter === p.id
                  ? 'border-[#00aaff] bg-[#00aaff]/5 shadow-sm'
                  : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
              data-testid={`admin-review-stat-${p.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`w-2 h-2 rounded-full ${p.color}`} />
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  {p.label}
                </span>
              </div>
              <p className="text-3xl font-black text-gray-900">{stats[p.id]}</p>
            </button>
          ))}
        </div>

        {/* Search + filter pills */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, email, produit, commentaire…)"
              className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              data-testid="admin-review-search"
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Filter size={14} /> {filtered.length}
            {filtered.length !== reviews.length && <> / {reviews.length}</>}
          </div>
        </div>
      </div>

      {/* Review list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-100">
          <Star size={42} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">Aucun avis ne correspond</p>
          <p className="text-xs text-gray-400 mt-1">
            {filter !== 'all' && `Filtre actif: ${STATUS_META[filter]?.label}`} {search && `· Recherche: "${search}"`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((review) => {
            const status = STATUS_META[review.status] || STATUS_META.pending;
            const photos = Array.isArray(review.photos) ? review.photos.filter(Boolean) : [];
            return (
              <div
                key={review.id}
                className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 transition-colors"
                data-testid={`admin-review-row-${review.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            size={14}
                            className={i <= review.rating ? 'text-[#00aaff]' : 'text-gray-200'}
                            fill={i <= review.rating ? '#00aaff' : '#e5e7eb'}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {review.author_name}
                      </span>
                      {review.author_email && (
                        <span className="text-xs text-gray-400">({review.author_email})</span>
                      )}
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${status.color}`}>
                        {status.label.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 italic leading-relaxed">
                      "{review.comment}"
                    </p>
                    {review.product_name && (
                      <p className="text-xs text-gray-400 mt-2">
                        <span className="font-semibold text-gray-500">Produit :</span> {review.product_name}
                      </p>
                    )}
                    {photos.length > 0 && (
                      <div className="mt-3">
                        <ReviewPhotos photos={photos} size="sm" maxThumbs={5} />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {review.status !== 'approved' && (
                      <button
                        type="button"
                        onClick={() => onQuickStatus(review, 'approved')}
                        title="Approuver"
                        className="w-9 h-9 bg-green-100 hover:bg-green-200 text-green-600 rounded-xl flex items-center justify-center transition-colors"
                        data-testid={`approve-review-${review.id}`}
                      >
                        <Check size={16} />
                      </button>
                    )}
                    {review.status !== 'rejected' && (
                      <button
                        type="button"
                        onClick={() => onQuickStatus(review, 'rejected')}
                        title="Refuser"
                        className="w-9 h-9 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-xl flex items-center justify-center transition-colors"
                        data-testid={`reject-review-${review.id}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(review);
                        setEditorOpen(true);
                      }}
                      title="Modifier"
                      className="w-9 h-9 bg-[#00aaff]/10 hover:bg-[#00aaff]/20 text-[#00aaff] rounded-xl flex items-center justify-center transition-colors"
                      data-testid={`edit-review-${review.id}`}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(review)}
                      title="Supprimer"
                      className="w-9 h-9 bg-red-100 hover:bg-red-200 text-red-600 rounded-xl flex items-center justify-center transition-colors"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ReviewEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={onSaved}
        initial={editing}
      />
    </div>
  );
}
